import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import HealthOnboarding from '../components/HealthOnboarding';
import userService from '../services/userService';
import LoadingSpinner from '../components/common/LoadingSpinner';
import toast from 'react-hot-toast';

const HealthOnboardingWrapper = ({ children }) => {
  const { user } = useAuth();
  const [healthProfile, setHealthProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    checkHealthProfile();
  }, [user]);

  const checkHealthProfile = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const response = await userService.getHealthProfile();
      const profile = response.data;
      setHealthProfile(profile);
      
      // Only show onboarding if:
      // 1. User has never seen onboarding before (first time after registration)
      // 2. AND profile is not complete
      // After user skips or completes once, they won't see it again
      if (!profile || (!profile.hasSeenOnboarding && !profile.isProfileComplete)) {
        setShowOnboarding(true);
      }
    } catch (error) {
      console.error('Error checking health profile:', error);
      // If there's an error fetching profile, show onboarding only on first login
      const isNewUser = !user.healthProfile || !user.healthProfile.hasSeenOnboarding;
      if (isNewUser) {
        setShowOnboarding(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    checkHealthProfile(); // Refresh profile data
  };

  const handleSkipOnboarding = async () => {
    try {
      // Mark that user has seen onboarding (so it won't show again)
      await userService.markOnboardingSeen();
      setShowOnboarding(false);
      toast.success('You can complete your health profile anytime from Settings');
    } catch (error) {
      console.error('Error marking onboarding as seen:', error);
      // Still hide onboarding even if API call fails
      setShowOnboarding(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (showOnboarding) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Welcome to Your Health Journey!
            </h1>
            <p className="text-lg text-gray-600 mb-4">
              Complete your health profile to get personalized insights and recommendations
            </p>
            <button
              onClick={handleSkipOnboarding}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Skip for now (you can complete this later in your profile)
            </button>
          </div>
          <HealthOnboarding onComplete={handleOnboardingComplete} />
        </div>
      </div>
    );
  }

  return children;
};

export default HealthOnboardingWrapper;